import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import serverService from '../services/serverService';
import { Card, Button, StatusCard } from '../components/common';

/**
 * Services Page - 9개 백엔드 서비스 상태 모니터링
 */
const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // 서비스 목록 (기본값)
  const defaultServices = [
    { name: 'Main API', port: 4000, pm2: 'ilouli-main-api', icon: '🏠' },
    { name: 'Auth', port: 4001, pm2: 'ilouli-auth-api', icon: '🔐' },
    { name: 'Community', port: 4002, pm2: 'ilouli-community-api', icon: '💬' },
    { name: 'Stock', port: 4003, pm2: 'ilouli-stock', icon: '📈' },
    { name: 'AI', port: 4004, pm2: 'ilouli-ai-api', icon: '🤖' },
    { name: 'Admin', port: 4005, pm2: 'ilouli-admin-api', icon: '⚙️' },
    { name: 'Family', port: 3006, pm2: 'ilouli-family-api', icon: '👨‍👩‍👧‍👦' },
    { name: 'Lab', port: 3007, pm2: 'ilouli-lab-api', icon: '🔬' },
    { name: 'DevTeam', port: 3009, pm2: 'ilouli-devteam', icon: '👨‍💻' },
  ];

  const loadServices = useCallback(async () => {
    setLoading(true);
    try {
      const result = await serverService.getPM2Processes();
      if (result.success) {
        const pm2List = result.data || [];
        const serviceStatus = defaultServices.map(service => {
          const pm2Process = pm2List.find(p => p.name === service.pm2);
          return {
            ...service,
            status: pm2Process?.status === 'online' ? 'online' : 'offline',
            cpu: pm2Process?.cpu || 0,
            memory: pm2Process?.memory || 0,
            uptime: pm2Process?.uptime || null,
            restarts: pm2Process?.restarts || 0,
            pid: pm2Process?.pid || null
          };
        });
        setServices(serviceStatus);
      } else {
        setServices(defaultServices.map(s => ({ ...s, status: 'unknown' })));
      }
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load services:', err);
      setServices(defaultServices.map(s => ({ ...s, status: 'unknown' })));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  // 자동 새로고침
  useEffect(() => {
    if (refreshInterval) {
      const interval = setInterval(loadServices, refreshInterval * 1000);
      return () => clearInterval(interval);
    }
  }, [refreshInterval, loadServices]);

  // 바이트 포맷팅
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0';
    const mb = bytes / (1024 * 1024);
    return Math.round(mb);
  };

  // 업타임 포맷팅
  const formatUptime = (uptime) => {
    if (!uptime) return '-';
    const now = Date.now();
    const diff = now - uptime;
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // 서비스 상태 통계
  const onlineCount = services.filter(s => s.status === 'online').length;
  const offlineCount = services.filter(s => s.status === 'offline').length;

  // 필터링된 서비스
  const filteredServices = services.filter(s => {
    if (statusFilter === 'all') return true;
    return s.status === statusFilter;
  });

  if (loading && services.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="w-10 h-10 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
        <p className="text-gray-600 text-sm">서비스 상태를 확인하는 중...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-[1400px] mx-auto max-md:gap-4">
      {/* 헤더 */}
      <div className="flex justify-between items-start gap-4 max-md:flex-col max-md:gap-4">
        <div>
          <h1 className="text-[28px] font-bold text-[#1d1d1f] m-0 mb-1 max-md:text-2xl">서비스 모니터링</h1>
          <p className="text-sm text-gray-600 m-0">9개 백엔드 서비스의 상태를 모니터링합니다</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 max-md:flex-wrap">
          {lastUpdated && (
            <span className="text-xs text-[#86868b] bg-[#f5f5f7] px-3 py-1.5 rounded-2xl">
              마지막 업데이트: {lastUpdated.toLocaleTimeString('ko-KR')}
            </span>
          )}
          <select
            value={refreshInterval || ''}
            onChange={(e) => setRefreshInterval(e.target.value ? parseInt(e.target.value) : null)}
            className="py-2 px-3 border border-gray-200 rounded-lg text-[13px] text-[#1d1d1f] bg-white cursor-pointer transition-all hover:border-gray-300 focus:outline-none focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10"
          >
            <option value="">수동 새로고침</option>
            <option value="5">5초마다</option>
            <option value="10">10초마다</option>
            <option value="30">30초마다</option>
          </select>
          <Button
            variant="secondary"
            onClick={loadServices}
            loading={loading}
          >
            새로고침
          </Button>
        </div>
      </div>

      {/* 상태 요약 */}
      <div className="grid grid-cols-4 gap-4 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <div
          className={`flex items-center gap-4 p-5 border rounded-xl cursor-pointer transition-all hover:translate-y-[-2px] hover:shadow-lg max-md:p-4 max-md:gap-3 ${
            statusFilter === 'online' ? 'bg-green-500 border-green-600' : 'bg-white border-gray-200'
          }`}
          onClick={() => setStatusFilter(statusFilter === 'online' ? 'all' : 'online')}
        >
          <span className={`text-[28px] leading-none flex-shrink-0 max-md:text-2xl ${statusFilter === 'online' ? 'grayscale brightness-[10]' : ''}`}>✅</span>
          <div className="flex flex-col gap-0.5">
            <span className={`text-2xl font-bold leading-tight max-md:text-xl ${statusFilter === 'online' ? 'text-white' : 'text-green-500'}`}>{onlineCount}</span>
            <span className={`text-[13px] ${statusFilter === 'online' ? 'text-white' : 'text-gray-600'}`}>온라인</span>
          </div>
        </div>
        <div
          className={`flex items-center gap-4 p-5 border rounded-xl cursor-pointer transition-all hover:translate-y-[-2px] hover:shadow-lg max-md:p-4 max-md:gap-3 ${
            statusFilter === 'offline' ? 'bg-red-500 border-red-600' : 'bg-white border-gray-200'
          }`}
          onClick={() => setStatusFilter(statusFilter === 'offline' ? 'all' : 'offline')}
        >
          <span className={`text-[28px] leading-none flex-shrink-0 max-md:text-2xl ${statusFilter === 'offline' ? 'grayscale brightness-[10]' : ''}`}>❌</span>
          <div className="flex flex-col gap-0.5">
            <span className={`text-2xl font-bold leading-tight max-md:text-xl ${statusFilter === 'offline' ? 'text-white' : 'text-red-500'}`}>{offlineCount}</span>
            <span className={`text-[13px] ${statusFilter === 'offline' ? 'text-white' : 'text-gray-600'}`}>오프라인</span>
          </div>
        </div>
        <div
          className={`flex items-center gap-4 p-5 border rounded-xl cursor-pointer transition-all hover:translate-y-[-2px] hover:shadow-lg max-md:p-4 max-md:gap-3 ${
            statusFilter === 'all' ? 'bg-blue-500 border-blue-600' : 'bg-white border-gray-200'
          }`}
          onClick={() => setStatusFilter('all')}
        >
          <span className={`text-[28px] leading-none flex-shrink-0 max-md:text-2xl ${statusFilter === 'all' ? 'grayscale brightness-[10]' : ''}`}>📊</span>
          <div className="flex flex-col gap-0.5">
            <span className={`text-2xl font-bold leading-tight max-md:text-xl ${statusFilter === 'all' ? 'text-white' : 'text-[#1d1d1f]'}`}>{services.length}</span>
            <span className={`text-[13px] ${statusFilter === 'all' ? 'text-white' : 'text-gray-600'}`}>전체 서비스</span>
          </div>
        </div>
        <div className="flex items-center gap-4 p-5 bg-white border border-gray-200 rounded-xl cursor-default max-md:p-4 max-md:gap-3">
          <span className="text-[28px] leading-none flex-shrink-0 max-md:text-2xl">⏰</span>
          <div className="flex flex-col gap-0.5">
            <span className="text-2xl font-bold text-[#1d1d1f] leading-tight max-md:text-xl">{refreshInterval ? `${refreshInterval}초` : '수동'}</span>
            <span className="text-[13px] text-gray-600">새로고침 주기</span>
          </div>
        </div>
      </div>

      {/* 서비스 그리드 */}
      <div className="grid grid-cols-3 gap-4 max-xl:grid-cols-2 max-md:grid-cols-1">
        {filteredServices.map((service) => (
          <Card key={service.name} className={`transition-all hover:translate-y-[-2px] hover:shadow-lg ${
            service.status === 'online' ? 'border-l-[3px] border-l-green-500' :
            service.status === 'offline' ? 'border-l-[3px] border-l-red-500' :
            'border-l-[3px] border-l-amber-500'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2.5">
                <span className="text-xl leading-none">{service.icon}</span>
                <span className="text-base font-semibold text-[#1d1d1f]">{service.name}</span>
              </div>
              <div className={`flex items-center gap-1.5 py-1 px-2.5 rounded-2xl ${
                service.status === 'online' ? 'bg-green-100' :
                service.status === 'offline' ? 'bg-red-100' :
                'bg-amber-100'
              }`}>
                <span className={`w-2 h-2 rounded-full ${
                  service.status === 'online' ? 'bg-green-500' :
                  service.status === 'offline' ? 'bg-red-500' :
                  'bg-amber-500'
                }`}></span>
                <span className={`text-xs font-medium ${
                  service.status === 'online' ? 'text-green-600' :
                  service.status === 'offline' ? 'text-red-600' :
                  'text-amber-600'
                }`}>
                  {service.status === 'online' ? '온라인' :
                   service.status === 'offline' ? '오프라인' : '확인중'}
                </span>
              </div>
            </div>

            <div className="flex gap-6 pb-3 border-b border-gray-100 mb-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-[11px] text-[#86868b] uppercase tracking-wider">PM2</span>
                <span className="text-[13px] font-medium text-[#1d1d1f]">{service.pm2}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[11px] text-[#86868b] uppercase tracking-wider">포트</span>
                <span className="text-[13px] font-medium text-[#1d1d1f]">{service.port}</span>
              </div>
            </div>

            {service.status === 'online' && (
              <div className="grid grid-cols-4 gap-3 max-md:grid-cols-2">
                <div className="flex flex-col items-center gap-0.5 p-2 bg-gray-50 rounded-lg">
                  <span className="text-[15px] font-semibold text-[#1d1d1f]">{service.cpu}%</span>
                  <span className="text-[10px] text-[#86868b] uppercase">CPU</span>
                </div>
                <div className="flex flex-col items-center gap-0.5 p-2 bg-gray-50 rounded-lg">
                  <span className="text-[15px] font-semibold text-[#1d1d1f]">{formatBytes(service.memory)}MB</span>
                  <span className="text-[10px] text-[#86868b] uppercase">메모리</span>
                </div>
                <div className="flex flex-col items-center gap-0.5 p-2 bg-gray-50 rounded-lg">
                  <span className="text-[15px] font-semibold text-[#1d1d1f]">{formatUptime(service.uptime)}</span>
                  <span className="text-[10px] text-[#86868b] uppercase">업타임</span>
                </div>
                <div className="flex flex-col items-center gap-0.5 p-2 bg-gray-50 rounded-lg">
                  <span className="text-[15px] font-semibold text-[#1d1d1f]">{service.restarts}</span>
                  <span className="text-[10px] text-[#86868b] uppercase">재시작</span>
                </div>
              </div>
            )}

            {service.status === 'offline' && (
              <div className="flex items-center justify-center p-4 bg-red-50 rounded-lg">
                <span className="text-[13px] text-red-600">서비스가 실행되지 않고 있습니다</span>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* 안내 */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <span className="text-xl leading-none flex-shrink-0">💡</span>
          <p className="m-0 text-sm text-[#1d1d1f] leading-relaxed">
            PM2 프로세스 재시작 및 로그 확인은{' '}
            <Link to="/server/pm2" className="text-blue-500 no-underline font-medium hover:underline">서버 관리 → PM2 프로세스</Link>
            에서 가능합니다.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default Services;
